'use client';

import { motion } from 'motion/react';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import { useRef, type KeyboardEvent } from 'react';

import { TrackedButton } from '@/components/button';
import { TRACKED_EVENT_POSITION } from '@/constants';
import { cn } from '@lib/utils';

import { StepCta } from './step-cta';
import { STEPS } from './steps';

// 스텝 행 높이(px). 패널 높이 625 = ROW_H × 5.
const ROW_H = 125;

const CTA_CLS =
  'inline-flex h-16 w-fit items-center justify-center rounded-[500px] bg-white px-8 text-[18px] font-bold leading-[30px] text-[#040435] shadow-[0_4px_16px_0_rgba(4,4,53,0.12)] transition-colors hover:bg-[#FF412D] hover:text-white';

// 데스크탑: 좌측 스텝 리스트(선택형) + 우측 이미지·CTA 패널.
export function DesktopSteps({
  active,
  setActive,
}: {
  active: number;
  setActive: (i: number) => void;
}) {
  const t = useTranslations('Products.process');
  const listRef = useRef<HTMLDivElement>(null);
  const ctaRef = useRef<HTMLDivElement>(null);

  // 스텝 리스트 roving: 활성 스텝만 Tab 정지점(tabIndex 0), 나머지는 -1.
  // ↑↓·←→ 로 활성 스텝을 옮기며 포커스도 함께 이동(하이라이트·이미지·CTA 갱신).
  // 스텝에 포커스한 채 Enter 를 누르면 그 스텝 사진 위 CTA(상세 링크/문의)를 바로 연다.
  // (버튼은 DOM 순서 = STEPS 순서)
  const focusStep = (index: number) => {
    const buttons =
      listRef.current?.querySelectorAll<HTMLButtonElement>('button');
    buttons?.[index]?.focus();
  };

  const moveTo = (index: number) => {
    const next = Math.min(Math.max(index, 0), STEPS.length - 1);
    if (next === active) return;
    setActive(next);
    focusStep(next);
  };

  // 활성 스텝의 CTA(링크 또는 문의 버튼)를 실제 클릭해 연다 — 추적·새 탭·문의 팝업 동작을 그대로 재사용
  const openActiveCta = () => {
    ctaRef.current?.querySelector<HTMLElement>('a, button')?.click();
  };

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    switch (e.key) {
      case 'ArrowDown':
      case 'ArrowRight':
        e.preventDefault();
        moveTo(active + 1);
        break;
      case 'ArrowUp':
      case 'ArrowLeft':
        e.preventDefault();
        moveTo(active - 1);
        break;
      case 'Home':
        e.preventDefault();
        moveTo(0);
        break;
      case 'End':
        e.preventDefault();
        moveTo(STEPS.length - 1);
        break;
      case 'Enter':
        // 스텝 버튼 자체의 활성(Enter→click=setActive)을 막고 CTA 를 연다
        e.preventDefault();
        openActiveCta();
        break;
    }
  };

  return (
    <div className="flex h-[625px] w-full gap-[44px]">
      <div
        ref={listRef}
        role="group"
        aria-labelledby="process-heading"
        onKeyDown={onKeyDown}
        className="relative h-full w-1/2 overflow-hidden rounded-3xl bg-gradient-to-b from-white to-[#DFEAFF]"
      >
        {/* 스텝 사이 구분선 (각 행 높이 = ROW_H) */}
        {[1, 2, 3, 4].map(n => (
          <div
            key={n}
            className="absolute inset-x-10 z-0 h-px bg-[#E4E5E9]"
            style={{ top: n * ROW_H }}
          />
        ))}
        {/* 활성 스텝 하이라이트 상자 — hover/클릭 시 위아래로 부드럽게 이동 */}
        <motion.div
          aria-hidden="true"
          className="absolute inset-x-0 top-[8px] z-[1] h-[110px] rounded-[20px] bg-[#DCE5F1]"
          initial={false}
          animate={{ y: active * ROW_H }}
          transition={{ type: 'spring', stiffness: 500, damping: 45 }}
        />
        {STEPS.map((step, i) => (
          // hover 로 선택(gtag 없음), 클릭 시 gtag 발화(_view)
          <TrackedButton
            key={step.key}
            type="button"
            label={`process_step-${step.key}_view`}
            position={TRACKED_EVENT_POSITION.MAIN_CONTENT}
            tabIndex={active === i ? 0 : -1}
            onClick={() => setActive(i)}
            onMouseEnter={() => setActive(i)}
            className="relative z-10 flex h-[125px] w-full items-center justify-between px-10 text-left"
          >
            <span className="text-2xl font-semibold leading-8 tracking-[-0.464px] text-[#575C66]">
              Step {i + 1}
            </span>
            <span className="text-[24px] font-semibold leading-[44px] tracking-[-0.784px] text-black">
              {t(`steps.${step.key}`)}
            </span>
          </TrackedButton>
        ))}
      </div>

      <div className="relative h-full w-1/2 overflow-hidden rounded-[20px] bg-[#121212]">
        {STEPS.map((step, i) => (
          <Image
            key={step.key}
            src={step.img}
            alt=""
            fill
            // 세로 패널(608×625)에 가로 이미지를 object-cover 로 채우면 높이 기준으로 확대되어
            // 폭(608px)보다 훨씬 큰 원본 픽셀이 필요하다. 원본 해상도를 서빙하도록 크게 지정.
            sizes="1920px"
            className={cn(
              'object-cover transition-opacity duration-500',
              active === i ? 'opacity-100' : 'opacity-0',
            )}
          />
        ))}
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-[rgba(116,174,255,0.4)]" />
        <div ref={ctaRef} className="absolute bottom-8 right-8">
          <StepCta step={STEPS[active]} className={CTA_CLS} />
        </div>
      </div>
    </div>
  );
}
